import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { ReverLoading } from '@itsrever/design-system'
import {
  isDashboardBlocked,
  isWarehousesV2Enabled,
  useFeatureFlag
} from '@/application/use-feature-flag'
import { useAppSelector } from '@/redux/hooks'
import { Roles } from '@/utils'
import NotFound from '@/ui/pages/404.page'
import { NavigationTabs, PageHeader } from '@/ui/shared'
import { BlockedDashboardOverlay } from '@/ui/components/overlays/blocked-dashboard-overlay'
import { Warehouses, WarehousesTab } from '@/ui/components/warehouses'
import {
  WarehousesScope,
  useWarehousesScope
} from '@/hooks/use-warehouses-scope'
import { useWarehousesTabs } from './configuration-tabs'

export default function WarehousesPage() {
  const { t } = useTranslation()
  const profile = useAppSelector((s) => s.userApi.getMe)
  // getMe is dispatched from an effect: deciding before it lands would flash the 404 at an admin.
  const loadingProfile =
    profile.loading === 'idle' || profile.loading === 'pending'
  const isAdmin = profile.response.user?.role === Roles.ReadWrite
  const { scope, showGroup, showStore } = useWarehousesScope()
  const tabs = useWarehousesTabs()
  const tab: WarehousesTab = useLocation().pathname.endsWith('/rules')
    ? 'rules'
    : 'list'

  if (loadingProfile) return <ReverLoading />
  if (!isAdmin) return <NotFound />
  return (
    <>
      <NavigationTabs tabs={tabs} selectedIndex={tab === 'rules' ? 1 : 0} />
      {scope.kind === 'none' ? (
        <>
          <PageHeader title={t('warehouses.title')} />
          <p className="px-7 py-6 text-sm text-neutral-600">
            {t('warehouses.no_scope')}
          </p>
        </>
      ) : (
        // Remounted per group, so the flag is evaluated once for the group on screen.
        <GroupLevelGate
          key={scope.group}
          scope={scope}
          tab={tab}
          onShowGroup={showGroup}
          onShowStore={showStore}
        />
      )}
    </>
  )
}

function GroupLevelGate({
  scope,
  tab,
  onShowGroup,
  onShowStore
}: {
  scope: Exclude<WarehousesScope, { kind: 'none' }>
  tab: WarehousesTab
  onShowGroup: () => void
  onShowStore: (ecommerceId: string) => void
}) {
  const { isFlagEnabled: blocked } = useFeatureFlag(isDashboardBlocked, {
    pageName: 'configuration'
  })
  // Until a group moves to group-level warehouses, edits here would reach nothing it reads.
  const { isFlagEnabled, isFlagLoading } = useFeatureFlag(
    isWarehousesV2Enabled,
    { group: scope.group }
  )
  if (isFlagLoading) return <ReverLoading />
  if (!isFlagEnabled) return <NotFound />
  return (
    <>
      {blocked && <BlockedDashboardOverlay />}
      <Warehouses
        scope={scope}
        tab={tab}
        onShowGroup={onShowGroup}
        onShowStore={onShowStore}
      />
    </>
  )
}
